import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  updateMe,
  updateMyProfilePicture,
  deleteMyProfilePicture,
  deleteMe,
} from "../../services/user.service";
import { setReduxValue, clearReduxValue } from "../../utils/redux";
import capitalize from "../../utils/capitalize";
import "../../styles/user/settings.css";

const Settings = () => {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  // ── Profile photo ──────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError("");
    setPhotoLoading(true);
    try {
      const updated = await updateMyProfilePicture(file);
      setReduxValue("user.user", updated);
    } catch (err) {
      setPhotoError(err.message || "Failed to upload photo");
    } finally {
      setPhotoLoading(false);
      e.target.value = "";
    }
  };

  const handleRemovePhoto = async () => {
    if (!confirm("Remove your profile photo?")) return;
    setPhotoError("");
    setPhotoLoading(true);
    try {
      const updated = await deleteMyProfilePicture();
      setReduxValue("user.user", updated);
    } catch (err) {
      setPhotoError(err.message || "Failed to remove photo");
    } finally {
      setPhotoLoading(false);
    }
  };

  // ── Edit profile ───────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    bio: user.bio || "",
    phoneNumber: user.phoneNumber || "",
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState(false);

  const handleProfileChange = (e) => {
    setProfile((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setProfileSuccess(false);
    setProfileError("");
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess(false);
    setProfileLoading(true);
    try {
      const updated = await updateMe(profile);
      setReduxValue("user.user", updated);
      setProfileSuccess(true);
    } catch (err) {
      setProfileError(err.message || "Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Delete account ─────────────────────────────────────────────────────────
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleDeleteAccount = async () => {
    if (!confirm("Permanently delete your account? This cannot be undone.")) return;
    if (!confirm("Are you absolutely sure? All your data will be lost.")) return;
    setDeleteError("");
    setDeleteLoading(true);
    try {
      await deleteMe();
      clearReduxValue("auth.accessToken");
      clearReduxValue("user.user");
      navigate("/", { replace: true });
    } catch (err) {
      setDeleteError(err.message || "Failed to delete account");
      setDeleteLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <h1 className="settings-title">Settings</h1>

      {/* ── Profile Photo ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Profile Photo</h2>
        <div className="settings-avatar-row">
          <div className="settings-avatar">
            {user.avatar ? (
              <img src={user.avatar} alt="avatar" />
            ) : (
              <div className="settings-avatar-fallback">{initials}</div>
            )}
          </div>
          <div className="settings-avatar-actions">
            <button
              className="settings-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={photoLoading}
            >
              {photoLoading ? "Uploading..." : "Upload Photo"}
            </button>
            {user.avatar && (
              <button
                className="settings-btn danger"
                onClick={handleRemovePhoto}
                disabled={photoLoading}
              >
                Remove
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handlePhotoChange}
            />
          </div>
        </div>
        {photoError && <p className="settings-error">{photoError}</p>}
      </section>

      {/* ── Edit Profile ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Edit Profile</h2>
        <form className="settings-form" onSubmit={handleProfileSave}>
          <div className="settings-row">
            <div className="settings-field">
              <label>First Name</label>
              <input
                name="firstName"
                value={profile.firstName}
                onChange={handleProfileChange}
                placeholder="First name"
              />
            </div>
            <div className="settings-field">
              <label>Last Name</label>
              <input
                name="lastName"
                value={profile.lastName}
                onChange={handleProfileChange}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className="settings-field">
            <label>Bio</label>
            <textarea
              name="bio"
              value={profile.bio}
              onChange={handleProfileChange}
              placeholder="Tell the world about yourself..."
              rows={3}
            />
          </div>
          <div className="settings-field">
            <label>Phone Number</label>
            <input
              name="phoneNumber"
              value={profile.phoneNumber}
              onChange={handleProfileChange}
              placeholder="+1 234 567 8900"
            />
          </div>
          {profileError && <p className="settings-error">{profileError}</p>}
          {profileSuccess && <p className="settings-success">Profile updated.</p>}
          <button className="settings-btn primary" type="submit" disabled={profileLoading}>
            {profileLoading ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </section>

      {/* ── Danger Zone ── */}
      <section className="settings-section danger-zone">
        <h2 className="settings-section-title">Danger Zone</h2>
        <p className="settings-danger-desc">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>
        {deleteError && <p className="settings-error">{deleteError}</p>}
        <button
          className="settings-btn danger"
          onClick={handleDeleteAccount}
          disabled={deleteLoading}
        >
          {deleteLoading ? "Deleting..." : "Delete Account"}
        </button>
      </section>
    </div>
  );
};

export default Settings;
